import EmObject from '@ember/object';
import { Promise as RSVPPromise } from 'rsvp';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { typeOf, isNone } from '@ember/utils';
import Ember from 'ember';

import isDestroyed from 'cio/utils/is-destroyed';
import { ApiResponse, ApiError } from 'cio/services/api';
import config from 'cio/config/environment';
import { BeeTimeoutError } from './bee';
import formatError from 'cio/utils/errors/format-error';

import AlertsService from 'cio/services/alerts';

export const SERVER_ERROR = 'serverError';
export const SERVICE_UNAVAILABLE = 'serviceUnavailable';
export const CLIENT_ERROR = 'clientError';
export const READ_ONLY_ERROR = 'readOnlyError';
export const EXTERNAL_API_FORBIDDEN_ERROR = 'externalForbiddenError';
export const VALIDATION_ERROR = 'validationError';
export const BEE_SAVE_TIMEOUT_ERROR = 'beeSaveTimeoutError';
export const UNATHORIZED_ERROR = 'unauthorized';

interface ApiErrorsServiceParsedError {
  type: string;
  messages?: string[];
  externalAPI?: boolean;
}

export default class ApiErrorsService extends Service {
  @service alerts!: AlertsService;

  current: ApiErrorsServiceParsedError | null = null;

  attach(Promise: RSVPPromise<any> | Promise<any>, reportOptions = {}): void {
    Promise.then(
      () => {
        if (!isDestroyed(this)) {
          this.clear();
        }
      },
      (response) => {
        if (!isDestroyed(this)) {
          this.report(response, reportOptions);
        }
      },
    );
  }

  report(Response: ApiResponse, { externalAPIError = false } = {}) {
    if (config.isDevelopment || Ember.testing) {
      // eslint-disable-next-line no-console
      console.error(Response);
    }

    if (this.handleByCode(Response)) {
      return;
    }

    let type = responseType(Response, { externalAPIError });
    let error: ApiErrorsServiceParsedError = { type };

    if (type !== SERVICE_UNAVAILABLE) {
      if (typeOf(Response.errors) === 'array') {
        error.messages = (Response.errors as ApiError[]).map((error) => error.detail);
        // @ts-ignore
      } else if (typeOf(Response.errors) === 'object') {
        error.messages = flattenErrorsObject(Response.errors as { [key: string]: string[] });
      }
    }

    if (externalAPIError) {
      error.externalAPI = true;
    }

    // Prevent errors to appear when user is not authenticated and password-modal is shown
    if (type !== UNATHORIZED_ERROR) {
      this.set('current', EmObject.create(error));
    }
  }

  handleByCode(Response: ApiResponse): boolean {
    let handled = false;
    if (typeOf(Response.errors) === 'array') {
      for (let e of Response.errors as ApiError[]) {
        if (!isNone(e.code)) {
          this.handleErrorByCode(e);
          handled = true;
        }
      }
    }

    return handled;
  }

  handleErrorByCode(e: ApiError) {
    switch (e.code) {
      default:
        this.alerts.error(String(e.code), formatError(e.code, 'api', null, null, e.detail));
    }
  }

  clear() {
    this.set('current', null);
  }
}

// Converts an errors object to a flat array of messages.
// flattenErrorsObject({ name: ['is bad', 'sucks'], face: ['is dumb'] })
// -> ['is bad', 'sucks', 'is dumb']
function flattenErrorsObject(errors: { [key: string]: string[] }): string[] {
  return Object.values(errors).reduce(function (messages, errorMessage) {
    return messages.concat(errorMessage);
  }, []);
}

function is5xxError(status: number): boolean {
  return /^5[0-9][0-9]$/.test(String(status));
}

function is503Error(status: number): boolean {
  return /^503$/.test(String(status));
}

function is4xxError(status: number): boolean {
  return /^4[0-9][0-9]$/.test(String(status));
}

function is422Error(status: number): boolean {
  return /^422$/.test(String(status));
}

function is401Error(status: number): boolean {
  return /^401$/.test(String(status));
}

function is403Error(status: number): boolean {
  return /^403$/.test(String(status));
}

function responseType(response: ApiResponse, { externalAPIError = false } = {}): string {
  if (response instanceof BeeTimeoutError && response.message?.includes('isSaving')) {
    return BEE_SAVE_TIMEOUT_ERROR;
  }

  // Empty response is almost definitely a server error
  if (isNone(response.errors)) {
    return SERVER_ERROR;
  }

  // eslint-disable-next-line
  let error = (response.errors as ApiError[])[0];

  if (isNone(error)) {
    // Promise rejection without an error (e.g. 422 with empty body)
    return CLIENT_ERROR;
  }

  let status = Number(error.status);

  if (is403Error(status)) {
    if (externalAPIError) {
      return EXTERNAL_API_FORBIDDEN_ERROR;
    } else {
      return READ_ONLY_ERROR;
    }
  } else if (is401Error(status)) {
    return UNATHORIZED_ERROR;
  } else if (is422Error(status)) {
    return VALIDATION_ERROR;
  } else if (is4xxError(status)) {
    return CLIENT_ERROR;
  } else if (is503Error(status)) {
    return SERVICE_UNAVAILABLE;
  } else if (is5xxError(status)) {
    return SERVER_ERROR;
  } else {
    // No status; must be a server-side validation error
    return VALIDATION_ERROR;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'api-errors': ApiErrorsService;
  }
}
